/**
 * AgentRoom — "live office" fiel ao protótipo (prototipo_agent_room.png).
 *
 * Layout de 3 colunas: ROSTER (AGENTES) · ESCRITÓRIO (agentes sentados nas mesas,
 * com etiqueta de nome, balão de fala da atividade e contorno brilhante no ativo;
 * área de descanso com sofás; plantas/decoração) · ATIVIDADE + LOG DETALHADO.
 *
 * Adaptação ao nosso stack (a referência usa sprites pixel-art + Canvas + Rust/
 * Tauri): CSS/SVG dirigido pelos eventos de runtime (/trace). Read-only, sem dep
 * nova. Paleta C, pt-BR, a11y.
 */
import { useMemo } from 'react';
import {
  Bot, Cpu, PenLine, BookOpen, Brain, Pause, CheckCircle2, AlertTriangle,
  Radio, ListChecks,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useExecutionTrace, useExecutionSquad } from '@/api/executions';
import { useAgentsActivity } from '@/api/agents';
import { deriveAgentRuntime } from '@/lib/agentRuntime';
import { EscritorioDosAgentes } from '@/ui/office3d/EscritorioDosAgentes';

const EXEC_TERMINAL = new Set(['done', 'completed', 'failed', 'cancelled', 'sla_expired']);

const STATUS_STYLE = {
  active:    { color: C.cyber,   icon: Cpu,           label: 'ativo' },
  typing:    { color: C.cyber,   icon: PenLine,       label: 'gerando' },
  reading:   { color: C.neon ?? C.cyber, icon: BookOpen, label: 'analisando' },
  thinking:  { color: C.quantum ?? C.cyber, icon: Brain, label: 'planejando' },
  waiting:   { color: C.warn,    icon: Pause,         label: 'aguardando' },
  failed:    { color: C.hot,     icon: AlertTriangle, label: 'falhou' },
  completed: { color: C.ok ?? C.cyber, icon: CheckCircle2, label: 'concluído' },
  idle:      { color: C.neutralMuted, icon: Bot,      label: 'ocioso' },
};

// Cores das etiquetas de nome (como no protótipo — uma por estação).
const PILL_COLORS = ['#3ECf8e', '#4E7FFF', '#E6A701', '#A855F7', '#FF8A3D', '#4E7FFF', '#22C1C3'];

// Estações (mesas) — posições em % do escritório, espelhando o protótipo.
const STATIONS = [
  { x: 27, y: 24 }, { x: 54, y: 24 },              // topo: Orchestrator, Planner
  { x: 26, y: 52 }, { x: 51, y: 52 },              // meio: Researcher, Data Analyst
  { x: 20, y: 82 }, { x: 44, y: 82 }, { x: 68, y: 82 }, // base: Risk, Executor, Reviewer
];

function initials(name) {
  return String(name || '?').split(/[\s-]+/).filter(Boolean).slice(0, 2)
    .map((w) => w[0]?.toUpperCase()).join('') || '?';
}

// ─── Custo por agente (SPEC-AGENTES-A4 FR-17) ─────────────────────────────────
//
// /agents/activity traz, por agente da execução, `custo_usd`, `tokens`, `turnos` e
// `duracao_s`. Qualquer um pode faltar (API antiga, jornada, rastro ausente): a sala
// mostra só o que veio, e nada quando nada veio.

function _num(v) {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Os números do agente, normalizados (null quando ausentes). */
export function custoDoAgente(src) {
  if (!src) return null;
  const custoUsd = _num(src.custoUsd ?? src.custo_usd);
  const tokens = _num(src.tokens);
  const turnos = _num(src.turnos);
  const duracaoS = _num(src.duracaoS ?? src.duracao_s);
  if (custoUsd === null && tokens === null && turnos === null && duracaoS === null) return null;
  return { custoUsd, tokens, turnos, duracaoS };
}

function _duracao(s) {
  const t = Math.round(s);
  if (t < 60) return `${t}s`;
  const m = Math.floor(t / 60);
  if (m < 60) return `${m}min ${t % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}min`;
}

/** "US$ 1,23 · 4.500 tokens · 12 turnos · 3min 5s" — só as partes presentes. */
export function textoDoCusto(src) {
  const c = custoDoAgente(src);
  if (!c) return null;
  const partes = [];
  if (c.custoUsd !== null) {
    partes.push(`US$ ${c.custoUsd.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  }
  if (c.tokens !== null) partes.push(`${c.tokens.toLocaleString('pt-BR')} tokens`);
  if (c.turnos !== null) partes.push(`${c.turnos.toLocaleString('pt-BR')} turno${c.turnos === 1 ? '' : 's'}`);
  if (c.duracaoS !== null) partes.push(_duracao(c.duracaoS));
  return partes.length ? partes.join(' · ') : null;
}

/** Junta aos agentes do runtime os números de /agents/activity desta execução. */
export function mesclarCustos(agents, atividade, executionId) {
  const porAgente = {};
  for (const a of atividade ?? []) {
    if (a && a.agent_id && (!executionId || a.execution_id === executionId)) {
      porAgente[a.agent_id] = a;
    }
  }
  return (agents ?? []).map((a) => {
    const c = custoDoAgente(porAgente[a.agentId]);
    return c ? { ...a, ...c } : a;
  });
}

function bubbleText(a) {
  if (a.activity) return a.activity;
  return {
    waiting: 'Aguardando dependência…', thinking: 'Planejando tarefas…',
    reading: 'Buscando informações…', typing: 'Gerando código…',
    active: 'Trabalhando…', completed: 'Concluído', failed: 'Falhou',
  }[a.status] || null;
}

// ─── Abaixo do escritório: os agentes, em colunas que aproveitam a largura ─────

function Roster({ agents, onFocus, focusId }) {
  return (
    <div style={{ background: C.blackElev, border: `1px solid ${C.blackBorder}`,
      borderRadius: 10, padding: 12, flex: '2 1 420px', minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em',
        color: C.neutralDim, marginBottom: 10 }}>AGENTES ({agents.length})</div>
      <div style={{ display: 'grid', gap: 4, alignContent: 'start', maxHeight: 300, overflowY: 'auto',
        gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
        {agents.map((a) => {
          const st = STATUS_STYLE[a.status] ?? STATUS_STYLE.idle;
          const focused = focusId === a.agentId;
          return (
            <button key={a.agentId} onClick={() => onFocus?.({ id: a.agentId, name: a.name })}
              title={a.atuou === false
                ? `${a.name} está no squad, mas não assinou nenhum evento nesta execução`
                : `Ver eventos de ${a.name}`}
              data-testid={a.atuou === false ? 'agente-sem-atuacao' : 'agente-que-atuou'}
              style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 8px',
                borderRadius: 8, cursor: 'pointer', textAlign: 'left', width: '100%',
                opacity: a.atuou === false ? 0.5 : 1,
                background: focused ? `${C.cyber}12` : 'transparent',
                border: `1px solid ${focused ? `${C.cyber}55` : 'transparent'}` }}>
              <span style={{ width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                display: 'grid', placeItems: 'center', background: `${st.color}22`,
                color: st.color, border: `1.5px solid ${st.color}`, fontWeight: 800, fontSize: 11 }}>
                {initials(a.name)}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 12.5, fontWeight: 600, color: C.neutral,
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.name}</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, color: st.color }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: st.color,
                    boxShadow: a.active ? `0 0 6px ${st.color}` : 'none' }} />
                  {a.atuou === false ? 'não atuou nesta execução' : st.label}
                </span>
                {textoDoCusto(a) && (
                  <span data-testid="custo-do-agente"
                    style={{ display: 'block', fontSize: 10, color: C.neutralMuted,
                      fontVariantNumeric: 'tabular-nums', whiteSpace: 'normal' }}>
                    {textoDoCusto(a)}
                  </span>
                )}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Coluna central: o escritório ─────────────────────────────────────────────

function Station({ a, idx, onFocus, focused }) {
  const st = STATUS_STYLE[a.status] ?? STATUS_STYLE.idle;
  const Icon = st.icon;
  const pill = PILL_COLORS[idx % PILL_COLORS.length];
  const pos = STATIONS[idx % STATIONS.length];
  const bubble = bubbleText(a);
  const bubbleLeft = pos.x > 55; // perto da borda direita → balão à esquerda

  return (
    <div style={{ position: 'absolute', left: `${pos.x}%`, top: `${pos.y}%`,
      transform: 'translate(-50%, -50%)', width: 120, zIndex: a.active ? 4 : 3 }}>
      {/* Etiqueta de nome */}
      <div style={{ position: 'absolute', top: -22, left: '50%', transform: 'translateX(-50%)',
        background: pill, color: '#0b0b0b', fontSize: 10.5, fontWeight: 800, padding: '2px 8px',
        borderRadius: 5, whiteSpace: 'nowrap', boxShadow: '0 2px 6px rgba(0,0,0,0.4)' }}>
        {a.name}
      </div>

      {/* Balão de fala */}
      {bubble && (
        <div style={{ position: 'absolute', top: -6,
          [bubbleLeft ? 'right' : 'left']: 58, width: 132,
          background: '#f5f4ef', color: '#1a1a1a', fontSize: 10, lineHeight: 1.3,
          padding: '6px 8px', borderRadius: 8, boxShadow: '0 3px 8px rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', gap: 5 }}>
          <Icon size={12} style={{ color: st.color, flexShrink: 0 }} aria-hidden="true" />
          <span style={{ overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical' }}>{bubble}</span>
        </div>
      )}

      {/* Personagem + mesa (com contorno brilhante se ativo) */}
      <button
        onClick={() => onFocus?.({ id: a.agentId, name: a.name })}
        aria-label={`Agente ${a.name} — ${st.label}`}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0,
          background: 'transparent', border: 'none', cursor: onFocus ? 'pointer' : 'default',
          width: '100%', padding: 0 }}
      >
        {/* avatar (cabeça/tronco) */}
        <div style={{ '--nava-ring': `${st.color}88`,
          width: 40, height: 40, borderRadius: '50%', display: 'grid', placeItems: 'center',
          background: `${st.color}22`, color: st.color,
          border: `2px solid ${focused ? C.cyber : st.color}`, fontWeight: 800, fontSize: 13,
          animation: a.active ? 'navaAgentRing 1.8s infinite' : 'none', zIndex: 2 }}>
          {initials(a.name)}
        </div>
        {/* mesa com monitor */}
        <div style={{ marginTop: -6, width: 78, height: 26, borderRadius: '4px 4px 6px 6px',
          background: `linear-gradient(#6b4f34, #4a3722)`,
          border: `1px solid #2f2318`,
          boxShadow: a.active ? `0 0 0 2px ${C.quantum ?? C.cyber}, 0 0 14px ${C.quantum ?? C.cyber}88` : 'none',
          position: 'relative', display: 'grid', placeItems: 'center' }}>
          <div style={{ width: 30, height: 16, background: '#0f1b1b', borderRadius: 2,
            border: `1px solid ${st.color}66`, boxShadow: `inset 0 0 6px ${st.color}44` }} />
        </div>
        {/* estado */}
        <div style={{ marginTop: 3, fontSize: 9, fontWeight: 800, letterSpacing: '0.05em',
          color: st.color, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 3 }}>
          {st.label}
          {(a.status === 'typing' || a.status === 'thinking') && (
            <span aria-hidden="true" style={{ display: 'inline-flex', gap: 2 }}>
              {[0, 1, 2].map((i) => (
                <span key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: st.color,
                  animation: `navaDot 1.2s ${i * 0.18}s infinite ease-in-out` }} />
              ))}
            </span>
          )}
        </div>
      </button>
    </div>
  );
}

function Office({ agents, onFocus, focusId }) {
  const seated = agents.slice(0, STATIONS.length);
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 420, height: 470, borderRadius: 12,
      overflow: 'hidden', border: `1px solid ${C.blackBorder}`,
      background:
        // parede (topo) + piso de madeira
        `linear-gradient(#241c16 0 64px, transparent 64px),` +
        `repeating-linear-gradient(90deg, #3a2c1e 0 46px, #34271a 46px 48px)`,
    }}>
      {/* parede: janelas + quadros */}
      <div aria-hidden="true" style={{ position: 'absolute', top: 12, left: 0, right: 0, height: 40 }}>
        {[8, 20, 74, 86].map((x) => (
          <span key={x} style={{ position: 'absolute', left: `${x}%`, top: 0, width: 46, height: 34,
            background: '#14343f', border: '2px solid #0c1f26', borderRadius: 2 }} />
        ))}
        {[40, 52, 64].map((x) => (
          <span key={x} style={{ position: 'absolute', left: `${x}%`, top: 6, width: 26, height: 22,
            background: '#2a2118', border: '2px solid #4a3722', borderRadius: 2 }} />
        ))}
      </div>

      {/* área de descanso (lounge) — sofás + tapete + planta */}
      <div aria-hidden="true" style={{ position: 'absolute', right: '3%', top: '30%', width: 150, height: 150 }}>
        <div style={{ position: 'absolute', inset: 12, background: `${C.neutralMuted}12`,
          borderRadius: 10 }} />
        <div style={{ position: 'absolute', top: 0, left: 20, width: 90, height: 22,
          background: '#26415a', borderRadius: 8 }} />
        <div style={{ position: 'absolute', top: 30, left: 0, width: 22, height: 80,
          background: '#26415a', borderRadius: 8 }} />
        <div style={{ position: 'absolute', top: 30, right: 0, width: 22, height: 80,
          background: '#26415a', borderRadius: 8 }} />
        <div style={{ position: 'absolute', bottom: 34, left: '50%', transform: 'translateX(-50%)',
          fontSize: 20 }}>🪴</div>
      </div>

      {/* plantas decorativas */}
      {[{ x: '2%', y: '20%' }, { x: '4%', y: '86%' }, { x: '92%', y: '86%' }].map((p, i) => (
        <div key={i} aria-hidden="true" style={{ position: 'absolute', left: p.x, top: p.y, fontSize: 22 }}>🪴</div>
      ))}
      {/* vasos na base (canteiro) */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '30%', right: '20%', bottom: 6,
        height: 14, background: '#2f5233', borderRadius: 4, opacity: 0.7 }} />

      {seated.map((a, i) => (
        <Station key={a.agentId} a={a} idx={i} onFocus={onFocus} focused={focusId === a.agentId} />
      ))}
    </div>
  );
}

// ─── Coluna direita: atividade + log ──────────────────────────────────────────

function _time(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 8);
}

function ActivityTimeline({ events }) {
  const recent = (events ?? []).filter((e) => e.message).slice(-12).reverse();
  return (
    <div style={{ background: C.blackElev, border: `1px solid ${C.blackBorder}`,
      borderRadius: 10, padding: 12, flex: '1 1 320px', minWidth: 0, maxHeight: 346,
      display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800,
        letterSpacing: '0.08em', color: C.neutralDim, marginBottom: 10, flexShrink: 0 }}>
        <Radio size={12} aria-hidden="true" /> ATIVIDADE
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto', flex: 1 }}>
        {recent.map((e, i) => (
          <div key={i} style={{ borderLeft: `2px solid ${C.cyber}`, paddingLeft: 8 }}>
            <div style={{ fontSize: 10, color: C.neutralMuted, fontVariantNumeric: 'tabular-nums' }}>
              {_time(e.occurred_at)} · {e.agent_id || '—'}
            </div>
            <div style={{ fontSize: 11.5, color: C.neutral, lineHeight: 1.3,
              overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {e.message}
            </div>
          </div>
        ))}
        {!recent.length && <div style={{ fontSize: 11, color: C.neutralMuted }}>Sem atividade ainda.</div>}
      </div>
    </div>
  );
}

/** LOG DETALHADO — full-width abaixo da sala, mais alto (aproveita a tela). */
function DetailedLog({ events }) {
  const log = (events ?? []).filter((e) => e.message).slice(-200).reverse();
  return (
    <div style={{ background: C.black, border: `1px solid ${C.blackBorder}`,
      borderRadius: 10, padding: 14, marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 800,
        letterSpacing: '0.08em', color: C.neutralDim, marginBottom: 10 }}>
        <ListChecks size={12} aria-hidden="true" /> LOG DETALHADO
        <span style={{ marginLeft: 'auto', fontSize: 10, color: C.neutralMuted }}>
          {log.length} linha{log.length === 1 ? '' : 's'}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2,
        maxHeight: '42vh', minHeight: 200, overflowY: 'auto' }}>
        {log.map((e, i) => (
          <div key={i} style={{ fontSize: 11.5, fontFamily: 'monospace', color: C.neutralDim,
            lineHeight: 1.5 }}>
            <span style={{ color: C.neutralMuted, fontVariantNumeric: 'tabular-nums', marginRight: 6 }}>
              {_time(e.occurred_at)}
            </span>
            <span style={{ color: C.cyber }}>[{e.agent_id || '—'}]</span> {e.message}
          </div>
        ))}
        {!log.length && <div style={{ fontSize: 11, color: C.neutralMuted }}>Sem log ainda.</div>}
      </div>
    </div>
  );
}

// ─── Corpo reaproveitável (usado pelo Mission Control E pela Sala GERAL) ──────
//
// Recebe já derivado: `agents` (runtime: {agentId,name,status,active,activity})
// e `events` (crus: {occurred_at, agent_id, message}). Assim tanto a visão
// por-execução (dirigida por /trace) quanto a visão GERAL (dirigida por
// /agents/activity) usam O MESMO escritório.
export function AgentRoomView({ agents = [], events = [], onFocusAgent, focusedAgent,
  rotuloDeAbrir }) {
  const focusId = focusedAgent?.id;
  return (
    <div>
      {/* O escritório ocupa a largura TODA; Agentes e Atividade ficam logo abaixo, lado a
          lado (pedido do dono, 2026-10-09: aproveitar o máximo de tela para a animação). */}
      <div style={{ display: 'flex' }}>
        {/* Escritório 3D (2026-10-08): os agentes andam entre as bancadas, a biblioteca, a
            mesa de planejamento e o café conforme o que fazem. Sem WebGL — ou por escolha da
            pessoa — fica o escritório 2D de sempre. */}
        <EscritorioDosAgentes
          agents={agents} focusId={focusId} onFocus={onFocusAgent}
          rotuloDeAbrir={rotuloDeAbrir}
          detalheDoAgente={(a) => textoDoCusto(a) || ''}
          plano={<Office agents={agents} onFocus={onFocusAgent} focusId={focusId} />}
        />
      </div>
      <div data-testid="agentes-e-atividade"
        style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap', marginTop: 12 }}>
        <Roster agents={agents} onFocus={onFocusAgent} focusId={focusId} />
        <ActivityTimeline events={events} />
      </div>
      <DetailedLog events={events} />
    </div>
  );
}

// ─── Tela ─────────────────────────────────────────────────────────────────────

export function AgentRoom({ executionId, onFocusAgent, focusedAgent }) {
  const poll = useMemo(
    () => (q) => (EXEC_TERMINAL.has(String(q.state.data?.execution?.status ?? '').toLowerCase()) ? false : 4000),
    [],
  );
  const trace = useExecutionTrace(executionId, { refetchInterval: poll });
  const squad = useExecutionSquad(executionId);

  const status = String(trace.data?.execution?.status ?? '').toLowerCase();
  const execTerminal = EXEC_TERMINAL.has(status);
  // custo/tokens/turnos/duração por agente (FR-17) — só enriquece; falha não afeta a sala
  const atividade = useAgentsActivity({ refetchInterval: execTerminal ? undefined : 5000 });
  const agents = useMemo(
    () => mesclarCustos(
      deriveAgentRuntime(trace.data?.events ?? [], squad.data?.nodes ?? [], execTerminal),
      atividade?.data?.agents,
      executionId,
    ),
    [trace.data, squad.data, execTerminal, atividade?.data, executionId],
  );

  if (trace.isLoading) return <LoadingSpinner label="Entrando na sala dos agentes..." />;
  if (trace.error) return <QueryError error={trace.error} onRetry={() => trace.refetch()} />;

  const working = agents.filter((a) => a.active).length;
  const live = !execTerminal;

  return (
    <div>
      {/* barra superior */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.neutral }}>Sala dos Agentes</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
          color: live ? C.cyber : C.neutralMuted, letterSpacing: '0.06em' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%',
            background: live ? C.cyber : C.neutralMuted, boxShadow: live ? `0 0 8px ${C.cyber}` : 'none' }} />
          {live ? 'EXECUTANDO' : (status || 'ocioso').toUpperCase()}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: C.neutralMuted }}>
          {agents.length} agentes · {agents.filter((a) => a.atuou).length} atuaram · {working} em atividade
        </span>
      </div>

      {/* corpo: roster · escritório · atividade + LOG DETALHADO */}
      <AgentRoomView
        agents={agents}
        events={trace.data?.events ?? []}
        onFocusAgent={onFocusAgent}
        focusedAgent={focusedAgent}
      />
    </div>
  );
}
